import React, { useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { TcsmAppConfigs } from '../../../../../global/models/configuration-models';
import { useNewHireSettings } from '../../new-hire/hooks/useNewHireSettings';
import { NewHireEnrollmentRoutes } from '../models/new-hire-routes';
import { clearNewHireEnrollmentState, setCurrentNewHireBenefitEnrollmentStep } from '../slice/new-hire-enrollment-slices';
import { unSetEmploymentState } from '../../employment/employment-slice';
import { unloadCompensationState } from '../../compensation/compensation-history/compensation-history-slice';
import { unloadPersonState } from '../../../../account-info/features/personal-info/personal-info-slice';
import { DemographicAppKeys, DemographicsContext } from '../../../../../global/models/demographics-model';
import { unloadRelationshipState } from '../../../../account-info/features/relationships/relationships-slice';
import { unloadDependentState } from '../../../../account-info/features/dependents/dependents-slice';
import { clearPensionInfo } from '../../pension-enrollment/pension-enrollment-slice';
import { ClearTcsmSessionStorage } from '../../../../../global/helpers/session-storage-helper';
import { CoverageEnrollment } from '../enrollment/coverage/coverage-enrollment';
import { useCurrentEmployerClientIdHook } from '../hooks/useCurrentEmployerId';
import { useAccountInfoSettings } from '../../../../account-info/hooks/useAccountInfoSettings';
import { useAnnualEnrollmentSettings } from '../../../../annual-enrollment/hooks/useAnnualEnrollmentSettings';
import { useDemographicsPerson } from '../../../../account-info/hooks/useDemographicsPerson';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { PersonalInformationApp } from '../../../../account-info/features/personal-info/personal-info';
import { NewHireMemberEnrollmentPersonalInfoWizard } from '../member/wizards/new-hire-member-enrollment-wizard';
import { AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { withForbidden } from '../../../../../global/components/HOCs/with-forbidden';
import { TcsmFeatures } from '../../../../../global/models/features-model';
import { withLayout } from '../../../../../global/components/HOCs/with-layout';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { withScrollTop } from '../../../../../global/components/HOCs/with-scroll-Top';
import AccountInfoContainer from '../../../../account-info/components/account-info-container';
import { MyRelationshipsApp } from '../../../../account-info/features/relationships/my-relationships';
import { MaritalStatusApp } from '../../../../account-info/features/marital-status/marital-status';
import { DependentInfo } from '../../../../account-info/features/dependents/dependent-info';
import GenericStepper from '../../../../../global/components/navigation/generic-stepper';
import { getDioApproverEnrollmentStepItems, getDioApproverEnrollmentSteps } from '../enrollment/helpers/coverage-display-helpers';
import { useBenefitEnrollmentStep } from '../hooks/useBenefitEnrollmentStep';
import { NewHireProps } from '../../../models/arc-epi-props';
import { NewHireStipEmploymentCompensationReview } from '../../new-hire/components/stip-employment-comp-fields-review';
import { useEmploymentInfo } from '../../employment/useEmployment';
import { useGetCurrentEmployeeFromEmployeeList } from '../hooks/useGetCurrentEmployeeFromEmployeeList';
import { useSpouseInformation } from '../../../../account-info/hooks/useSpouseInfo';
import { useCompensationState } from '../hooks/useCompensationState';
import { AnnualEnrollmentRoutes } from '../../../../annual-enrollment/constants/annual-enrollment-routes';
import { NewHireEnrollmentConfirmation } from '../confirmation/new-hire-confirmation';


interface Props {
	appSettings: TcsmAppConfigs;
}

const PersonalInfoWrapper = withScrollTop()(withLayout(null, GpsType.NEW_HIRE_PERSONAL_INFO)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer)));
const RelationshipsWrapper = withScrollTop()(withLayout(null, GpsType.NEW_HIRE_RELATIONSHIP)(withForbidden(TcsmFeatures.Relationships)(AccountInfoContainer)));
const MaritalStatusWrapper = withLayout(null, GpsType.MARITAL_STATUS)(withForbidden(TcsmFeatures.MaritalStatus)(AccountInfoContainer));
const DependentsWrapper = withLayout(null, GpsType.ADD_DEPENDENT)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));
const EmploymentWrapper = withLayout(null, GpsType.EMPLOYMENT_COMPENSATION)(withForbidden(TcsmFeatures.PersonalInfo)(AccountInfoContainer));

function DioApproverEnrollmentApp(props: Props) {

	const { appSettings } = props;
	const settingsLoaded = useNewHireSettings(appSettings?.newHireSettingsId);
	const newHireProps = useSelector<TcsmStates, NewHireProps>(state => state?.epiProps?.newHireSettingsState?.epiContent);
	const epiApiState = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const routeMatch = useRouteMatch();
	const dispatch = useDispatch();
	const app = useContext(DemographicsContext);
	const demographicsSettingsLoaded = useAccountInfoSettings(appSettings.personalInfoSettingId, appSettings.relationshipsSettingId);
	const aeSettingsLoaded = useAnnualEnrollmentSettings(appSettings.annualEnrollmentSettingsId);
	const personLoaded = useDemographicsPerson(app);
	const history = useHistory();
	const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
	const spousesLoaded = useSpouseInformation(personGuid);
	useGetCurrentEmployeeFromEmployeeList();
	useEmploymentInfo();
	const compensationLoaded = useCompensationState();
	const employmentPrevStep = `${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`;
	const employmentNextStep = `${routeMatch.path}${NewHireEnrollmentRoutes.Coverage}`;
	const dioApproverNavigationLinksObject = {
		employmentPrevStep,
		employmentNextStep,
	};
	const isLifeEventCoverageForm = useSelector<TcsmStates, boolean>(state => state?.newHireEnrollmentState?.isLifeEventCoverageForm);

	useEffect(() => {
		return function cleanUp() {

			dispatch(unSetEmploymentState());
			dispatch(unloadCompensationState());
			dispatch(unloadPersonState(DemographicAppKeys.arc));
			dispatch(unloadRelationshipState(DemographicAppKeys.arc));
			dispatch(unloadDependentState(DemographicAppKeys.arc));
			dispatch(clearNewHireEnrollmentState());
			dispatch(clearPensionInfo());
			ClearTcsmSessionStorage(false);
		};
	}, []);

	const wizard = new NewHireMemberEnrollmentPersonalInfoWizard(epiApiState, AnnualEnrollmentKey.newHireEnrollment, app, dispatch, history);

	if (!settingsLoaded || !demographicsSettingsLoaded || !aeSettingsLoaded || !personLoaded || !compensationLoaded || !spousesLoaded) {
		return <LoadingCircle />;
	}


	return (
		<>
			<Switch>
				<AnnualEnrollmentContext.Provider value={AnnualEnrollmentKey.dioApproverEnrollment}>
					<GenericStepper
						basePath={routeMatch.path}
						allSteps={getDioApproverEnrollmentSteps(isLifeEventCoverageForm)}
						useCurrentStep={useBenefitEnrollmentStep}
						setCurrentStep={setCurrentNewHireBenefitEnrollmentStep}
						progressItems={getDioApproverEnrollmentStepItems(newHireProps, isLifeEventCoverageForm)}
					/>
					<Route path={`${routeMatch.path}${NewHireEnrollmentRoutes.Coverage}`} render={() =>
						<CoverageEnrollment
							basePath={routeMatch.path}
							queryString='employer_id'
							getEmployerIdHook={useCurrentEmployerClientIdHook}
							isDioApprover
						/>} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.PersonalInfoSegment}`} render={() => (
						<PersonalInfoWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<PersonalInformationApp basePath={routeMatch.path} customPersonalInfoWizard={wizard} isDioApprover />
						</PersonalInfoWrapper>
					)} />
					<Route exact path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} render={() => (
						<RelationshipsWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<MyRelationshipsApp basePath={routeMatch.path} />
						</RelationshipsWrapper>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`} render={() => (
						<MaritalStatusWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<MaritalStatusApp basePath={routeMatch.path} />
						</MaritalStatusWrapper>
					)} />
					<Route path={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.DependentSegment}`} render={() => (
						<DependentsWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<DependentInfo relationshipsPageRoute={`${routeMatch.path}${AccountInfoRoutes.RelationshipsSegment}`} />
						</DependentsWrapper>
					)} />
					<Route path={`${routeMatch.path}${AnnualEnrollmentRoutes.Confirmation}`}
						render={() => (
							<NewHireEnrollmentConfirmation settignsId={appSettings.newHireSettingsId} hideBreadCrumbs />
						)}
					/>
					<Route exact path={`${routeMatch.path}${AccountInfoRoutes.EmploymentSegment}`} render={() => (
						<EmploymentWrapper basePath={routeMatch.path} hideBreadcrumbs>
							<NewHireStipEmploymentCompensationReview newHireProps={newHireProps} dioApproverNavigationLinks={dioApproverNavigationLinksObject}/>
						</EmploymentWrapper >
					)} />
				</AnnualEnrollmentContext.Provider>
			</Switch>
		</>
	);
}

export default DioApproverEnrollmentApp;